"use client";

import { env } from "../../helpers/env.mjs";
import loadScript from "../../helpers/loadScript";
import classNames from "classnames/bind";
import { useEffect } from "react";
import styles from "./Header.module.css";

const cx = classNames.bind(styles);

interface MenuConfig {
  locale: string;
  theme: "dark" | "light";
  opaque: boolean;
  accountsDeactivated: boolean;
}
/**
 * This Header component implements the instructions in the Red Bull Platforms
 * documentation for embedding the redbull.com navigation menu. You can read
 * more about that here:
 * @see {@link https://platforms.redbull.com/develop/custom-development/microsites/headers}
 */

export const Header = ({
  dir,
  localeString,
  className,
  theme,
}: {
  dir: "ltr" | "rtl";
  localeString: string;
  className?: string;
  theme?: MenuConfig["theme"];
}) => {
  // Previously `theme` and `opaque` were configuable in the CMS
  const menuConfig: MenuConfig = {
    locale: localeString,
    theme: theme || "light", // Fallback to light if no theme provided
    opaque: true,
    accountsDeactivated: localeString === "cn-zh" ? true : false,
  };

  useEffect(() => {
    if (window) {
      // TODO: should this just be hoisted to the top level of this module?
      window.RBGEMC_ACCOUNTS_ENV = env.NEXT_PUBLIC_RBGEMC_ACCOUNTS_ENV;
      window.RBGEMC_ACCOUNTS_BUNDLE = env.NEXT_PUBLIC_RBGEMC_ACCOUNTS_BUNDLE;
    }
  }, []);

  useEffect(() => {
    loadScript(
      dir === "rtl"
        ? "https://www.redbull.com/v3/widgets/menu.rtl.js"
        : "https://www.redbull.com/v3/widgets/menu.js",
    );
  }, [dir]);

  // Swap theme classes when theme changes, used on CRH;
  /**
   * HACK: As we need to wait for the script to be loaded, we use a timeout as a simple hack solution.
   * perhaps longer code with a MutationObserver would be better, but the changing theme is an edge case.
   */
  useEffect(() => {
    // Only run theme switching when theme is explicitly provided
    if (typeof window !== "undefined" && theme) {
      let retryCount = 0;
      const maxRetries = 50; // 5 seconds total (50 * 100ms)

      const updateThemeClasses = () => {
        // Find elements that already have theme classes applied by the Red Bull script
        const elementsWithTheme = document.querySelectorAll(
          ".rb3-dark-theme, .rb3-light-theme",
        );

        if (elementsWithTheme.length > 0) {
          elementsWithTheme.forEach((element) => {
            // Remove both theme classes first
            element.classList.remove("rb3-dark-theme", "rb3-light-theme");
            // Add the appropriate theme class
            element.classList.add(`rb3-${theme}-theme`);
          });
        } else if (retryCount < maxRetries) {
          retryCount++;
          // Retry after a short delay if the script hasn't applied theme classes yet
          setTimeout(updateThemeClasses, 100);
        } else {
          console.warn(
            `Header theme update failed: Header script didn't load theme classes after ${maxRetries} attempts`,
          );
        }
      };

      // Start the update process
      updateThemeClasses();
    }
  }, [theme]);

  return (
    <div className={cx("container", className)}>
      <div
        id="rb3-global-menu" /* <- This `id` is a binding for the menu.js widget: don't remove */
      >
        <div
          className="rb3-global-menu__wrapper" /* <- This `className` is a binding for the menu.js widget: don't remove. */
        >
          <script
            type="text/props"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(menuConfig) }}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * This is a bit of technical debt, honestly. If you need to forcefully hide the
 * menu, then you'd use this component anywhere on the page.
 */
export const HeaderHider = () => <div className={cx("hider")} />;
